import React, { useCallback, useEffect, useState } from "react";
import { Wallet, Users, TrendingUp, Banknote, Link2, Clock, CheckCircle2, ArrowUpRight, FileText, Upload, Trash2, Eye, Loader2, LayoutGrid, List, Award, Gift, Star, Copy } from "lucide-react";
import api from "../lib/api";
import KanbanBoard from "../components/KanbanBoard";
import PortalShell from "./PortalShell";
import { useToast } from "../hooks/use-toast";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const STATUS_COLORS = {
  New: "bg-blue-100 text-blue-700", Contacted: "bg-amber-100 text-amber-700",
  Qualified: "bg-purple-100 text-purple-700", Applied: "bg-cyan-100 text-cyan-700",
  Admitted: "bg-green-100 text-green-700", Deposit: "bg-teal-100 text-teal-700",
  Visa: "bg-indigo-100 text-indigo-700", Closed: "bg-slate-100 text-slate-600", Lost: "bg-red-100 text-red-700",
};
const PAYOUT_COLORS = { Requested: "bg-amber-100 text-amber-700", Paid: "bg-green-100 text-green-700", Rejected: "bg-red-100 text-red-700" };
const COMM_COLORS = { Pending: "bg-amber-100 text-amber-700", Approved: "bg-blue-100 text-blue-700", Paid: "bg-green-100 text-green-700" };
const LEAD_STATUSES = ["New", "Contacted", "Qualified", "Applied", "Admitted", "Deposit", "Visa", "Closed", "Lost"];
const STATUS_TINT = {
  New: "#2563eb", Contacted: "#d97706", Qualified: "#7c3aed", Applied: "#0891b2",
  Admitted: "#16a34a", Deposit: "#0d9488", Visa: "#4f46e5", Closed: "#64748b", Lost: "#dc2626",
};

const money = (v, c) => `${c === "USD" ? "$" : c + " "}${Number(v || 0).toLocaleString()}`;

function Stat({ icon: Icon, label, value, tint = "#004030" }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5">
      <div className="w-11 h-11 rounded-xl grid place-items-center text-[#e0c05a]" style={{ background: tint }}><Icon size={20} /></div>
      <p className="font-display text-2xl font-bold text-[#012a1e] mt-3">{value}</p>
      <p className="text-slate-500 text-sm">{label}</p>
    </div>
  );
}

export default function AgentPortal() {
  const { toast } = useToast();
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("overview");
  const [leadView, setLeadView] = useState("table");
  const [payOpen, setPayOpen] = useState(false);
  const [pay, setPay] = useState({ amount: "", method: "Stripe Connect (bank)", notes: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => { api.get("/portal/agent/overview").then((r) => setD(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const [refs, setRefs] = useState(null);
  const [docs, setDocs] = useState([]);
  const [partners, setPartners] = useState(null);
  const [uploading, setUploading] = useState(false);
  useEffect(() => {
    if (tab === "referrals" && !refs) api.get("/portal/agent/referrals").then((r) => setRefs(r.data)).catch(() => {});
    if (tab === "documents") api.get("/portal/agent/documents").then((r) => setDocs(r.data)).catch(() => {});
    if (tab === "offers" && !partners) api.get("/universities").then((r) => setPartners((r.data || []).filter((u) => u.exclusive))).catch(() => {});
  }, [tab, refs, partners]);

  const docViewUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/portal/agent/documents/${id}/view?auth=${localStorage.getItem("gdih_token")}`;
  const uploadDoc = async (e) => {
    const file = (e.target.files || [])[0]; e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/portal/agent/documents?title=${encodeURIComponent(file.name)}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await api.get("/portal/agent/documents"); setDocs(data);
      toast({ title: "Document uploaded" });
    } catch (err) { toast({ title: "Upload failed", description: err?.response?.data?.detail || "", variant: "destructive" }); }
    finally { setUploading(false); }
  };
  const deleteDoc = async (id) => { if (!window.confirm("Delete this document?")) return; await api.delete(`/portal/agent/documents/${id}`); setDocs((d) => d.filter((x) => x.id !== id)); toast({ title: "Deleted" }); };

  const cur = d?.wallet?.currency || "USD";

  const agentPct = d?.profile?.commission_pct ?? 0;
  const printReport = () => {
    const rows = (d?.commissions || []).map((c) => {
      const pct = c.pct ?? agentPct;
      const fee = c.referral_fee ?? "";
      return `<tr><td>${c.reference || "Commission"}</td><td>${c.created_at ? new Date(c.created_at).toLocaleDateString() : ""}</td><td style="text-align:right">${fee !== "" ? (c.currency || cur) + " " + Number(fee).toLocaleString() : "—"}</td><td style="text-align:center">${pct}%</td><td style="text-align:right;font-weight:bold">${(c.currency || cur)} ${Number(c.amount || 0).toLocaleString()}</td><td>${c.status || ""}</td></tr>`;
    }).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Commission Report</title>
      <style>body{font-family:Georgia,serif;color:#012a1e;padding:32px;max-width:820px;margin:auto}
      h1{color:#004030;border-bottom:3px solid #004030;padding-bottom:10px;font-size:22px}
      .sub{color:#64756d;font-size:13px;margin:4px 0 18px}
      table{width:100%;border-collapse:collapse;font-size:13px}
      th{background:#f0f6f3;color:#004030;text-align:left;padding:8px;font-size:11px;text-transform:uppercase;letter-spacing:.5px}
      td{padding:8px;border-bottom:1px solid #eef2f0}
      .foot{margin-top:24px;font-size:11px;color:#8a9a92;text-align:center}</style></head>
      <body><h1>GDIH Elite Education — Commission Report</h1>
      <p class="sub">Agent: ${d?.profile?.name || ""} (${d?.profile?.agent_code || ""}) · Your rate: ${agentPct}%<br/>Commission = your % × each university's referral fee.</p>
      <table><thead><tr><th>Reference</th><th>Date</th><th>Referral fee</th><th>Your %</th><th>Commission</th><th>Status</th></tr></thead><tbody>${rows || '<tr><td colspan="6">No commissions yet.</td></tr>'}</tbody></table>
      <p class="sub" style="margin-top:14px">Approved: ${money(d?.wallet?.approved_total, cur)} · Pending: ${money(d?.wallet?.pending, cur)}</p>
      <div class="foot">Generated ${new Date().toLocaleString()} · GDIH Elite Education</div></body></html>`;
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 350);
  };
  const requestPayout = async () => {
    const amt = parseFloat(pay.amount);
    if (!amt || amt <= 0) { toast({ title: "Enter a valid amount", variant: "destructive" }); return; }
    setBusy(true);
    try {
      await api.post("/portal/agent/payout-request", { amount: amt, method: pay.method, notes: pay.notes });
      toast({ title: "Payout requested", description: "Our finance team will process it shortly." });
      setPayOpen(false); setPay({ amount: "", method: "Stripe Connect (bank)", notes: "" }); load();
    } catch (e) {
      toast({ title: "Request failed", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setBusy(false); }
  };

  const connectStripe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/portal/agent/connect/onboard", { origin: window.location.origin });
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast({ title: "Could not start onboarding", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <PortalShell allow="agent">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-[#012a1e]" data-testid="agent-portal-title">Hello, {d?.profile?.name?.split(" ")[0] || ""} 👋</h1>
          <p className="text-slate-500 mt-1">Your leads, conversions and commission wallet.
            {d?.profile?.agent_code && <span className="ml-2 font-mono text-xs bg-[#f0f6f3] text-[#004030] px-2 py-0.5 rounded" data-testid="agent-code">{d.profile.agent_code}</span>}</p>
        </div>
      </div>

      <div className="flex gap-1 bg-white border border-slate-100 rounded-full p-1 mt-5 w-fit">
        {[{ k: "overview", l: "Overview" }, { k: "leads", l: "My Leads" }, { k: "referrals", l: "Referrals" }, { k: "offers", l: "Exclusive Partners" }, { k: "report", l: "Commission Report" }, { k: "documents", l: "Documents" }, { k: "wallet", l: "Wallet & Payouts" }].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`agent-tab-${t.k}`}
            className={`text-sm font-medium px-4 py-2 rounded-full transition-colors ${tab === t.k ? "bg-[#004030] text-white" : "text-[#004030]"}`}>{t.l}</button>
        ))}
      </div>

      {tab === "overview" && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            <Stat icon={Users} label="Leads sourced" value={d?.stats?.total ?? 0} />
            <Stat icon={TrendingUp} label="Converted" value={d?.stats?.converted ?? 0} tint="#0a5540" />
            <Stat icon={ArrowUpRight} label="Conversion" value={`${d?.stats?.conversion_rate ?? 0}%`} tint="#c9a227" />
            <Stat icon={Wallet} label="Available balance" value={money(d?.wallet?.available, cur)} />
          </div>
          <div className="bg-white rounded-2xl border border-slate-100 p-6 mt-6">
            <h2 className="font-semibold text-[#012a1e] mb-4">Pipeline</h2>
            <div className="space-y-2.5">
              {Object.entries(d?.stats?.by_stage || {}).map(([k, v]) => {
                const max = Math.max(1, ...Object.values(d?.stats?.by_stage || { a: 1 }));
                return (
                  <div key={k} className="flex items-center gap-3">
                    <div className="w-24 shrink-0 text-sm text-slate-600">{k}</div>
                    <div className="flex-1 bg-slate-100 rounded-full h-5 overflow-hidden">
                      <div className="h-full rounded-full bg-[#004030] flex items-center justify-end pr-2 text-white text-xs font-semibold" style={{ width: `${Math.max((v / max) * 100, v ? 10 : 0)}%` }}>{v || ""}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {tab === "leads" && (
        <div className="mt-6">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-slate-500">{(d?.leads || []).length} lead{(d?.leads || []).length === 1 ? "" : "s"} assigned to you</p>
            <div className="flex rounded-full border border-slate-200 overflow-hidden bg-white" data-testid="agent-leads-view-toggle">
              <button onClick={() => setLeadView("table")} title="Table view" className={`px-3 py-1.5 ${leadView === "table" ? "bg-[#004030] text-white" : "text-[#004030]"}`} data-testid="agent-leads-view-table"><List size={15} /></button>
              <button onClick={() => setLeadView("kanban")} title="Kanban view" className={`px-3 py-1.5 ${leadView === "kanban" ? "bg-[#004030] text-white" : "text-[#004030]"}`} data-testid="agent-leads-view-kanban"><LayoutGrid size={15} /></button>
            </div>
          </div>
          {leadView === "kanban" ? (
            <KanbanBoard
              columns={LEAD_STATUSES}
              items={d?.leads || []}
              getId={(l) => l.id}
              getStatus={(l) => l.status}
              columnTint={STATUS_TINT}
              renderCard={(l) => (
                <div>
                  <p className="font-semibold text-sm text-[#1a231f]">{l.name}</p>
                  <p className="text-xs text-slate-500 truncate">{l.course || "—"}</p>
                  <p className="text-[10px] text-slate-400 mt-1.5">{l.country || "—"}</p>
                </div>
              )}
            />
          ) : (
          <div className="bg-white rounded-2xl border border-slate-100 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-[#f7faf8] text-left text-slate-500">
              <th className="px-4 py-3 font-semibold">Name</th><th className="px-4 py-3 font-semibold">Course</th>
              <th className="px-4 py-3 font-semibold">Country</th><th className="px-4 py-3 font-semibold">Status</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {(d?.leads || []).map((l) => (
                <tr key={l.id} data-testid={`agent-lead-${l.id}`}>
                  <td className="px-4 py-3"><p className="font-medium text-[#012a1e]">{l.name}</p><p className="text-xs text-slate-400">{l.email}</p></td>
                  <td className="px-4 py-3 text-slate-600">{l.course || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{l.country || "—"}</td>
                  <td className="px-4 py-3"><span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_COLORS[l.status] || "bg-slate-100"}`}>{l.status}</span></td>
                </tr>
              ))}
              {!(d?.leads || []).length && <tr><td colSpan={4} className="px-4 py-10 text-center text-slate-400">No leads assigned to you yet.</td></tr>}
            </tbody>
          </table>
          </div>
          )}
        </div>
      )}

      {tab === "wallet" && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            <Stat icon={Wallet} label="Available" value={money(d?.wallet?.available, cur)} />
            <Stat icon={Clock} label="Pending approval" value={money(d?.wallet?.pending, cur)} tint="#c9a227" />
            <Stat icon={Banknote} label="Paid out" value={money(d?.wallet?.paid_out, cur)} tint="#0a5540" />
            <Stat icon={CheckCircle2} label="Approved total" value={money(d?.wallet?.approved_total, cur)} />
          </div>

          <div className="flex items-center gap-3 mt-5 flex-wrap">
            <Button data-testid="request-payout-btn" onClick={() => setPayOpen(true)} disabled={!d?.wallet?.available}
              className="bg-[#004030] hover:bg-[#013d2b] text-white rounded-full px-6 h-11"><Banknote size={17} className="mr-1.5" /> Request payout</Button>
            {d?.connect?.payouts_enabled ? (
              <span className="text-sm text-green-700 flex items-center gap-1.5"><CheckCircle2 size={16} /> Stripe connected — bank payouts enabled</span>
            ) : (
              <Button data-testid="connect-stripe-btn" variant="outline" onClick={connectStripe} disabled={busy}
                className="rounded-full px-6 h-11 border-[#004030] text-[#004030]"><Link2 size={16} className="mr-1.5" /> {d?.connect?.connected ? "Finish Stripe setup" : "Connect bank (Stripe)"}</Button>
            )}
          </div>

          <div className="grid lg:grid-cols-2 gap-6 mt-6">
            <div className="bg-white rounded-2xl border border-slate-100 p-5">
              <h2 className="font-semibold text-[#012a1e] mb-3">Commission ledger</h2>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {(d?.commissions || []).map((c) => (
                  <div key={c.id} className="flex items-center justify-between border-b border-slate-50 pb-2" data-testid={`commission-${c.id}`}>
                    <div><p className="text-sm text-[#012a1e]">{c.reference || "Commission"}</p><p className="text-xs text-slate-400">{new Date(c.created_at).toLocaleDateString()}</p></div>
                    <div className="text-right"><p className="font-semibold text-[#012a1e]">{money(c.amount, c.currency || cur)}</p><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${COMM_COLORS[c.status] || "bg-slate-100"}`}>{c.status}</span></div>
                  </div>
                ))}
                {!(d?.commissions || []).length && <p className="text-sm text-slate-400">No commissions yet. They accrue when your leads convert to applications.</p>}
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-slate-100 p-5">
              <h2 className="font-semibold text-[#012a1e] mb-3">Payout history</h2>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {(d?.payouts || []).map((p) => (
                  <div key={p.id} className="flex items-center justify-between border-b border-slate-50 pb-2" data-testid={`payout-${p.id}`}>
                    <div><p className="text-sm text-[#012a1e]">{p.method}</p><p className="text-xs text-slate-400">{new Date(p.created_at).toLocaleDateString()}</p></div>
                    <div className="text-right"><p className="font-semibold text-[#012a1e]">{money(p.amount, p.currency || cur)}</p><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${PAYOUT_COLORS[p.status] || "bg-slate-100"}`}>{p.status}</span></div>
                  </div>
                ))}
                {!(d?.payouts || []).length && <p className="text-sm text-slate-400">No payouts yet.</p>}
              </div>
            </div>
          </div>
        </>
      )}

      {tab === "referrals" && (
        <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="agent-referrals">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <div>
              <h2 className="font-display text-lg font-bold text-[#012a1e]">Referred Students</h2>
              <p className="text-sm text-slate-500">Your referral code: <span className="font-bold text-[#004030]">{refs?.agent_code || d?.profile?.agent_code || "—"}</span> · Commission {refs?.commission_pct ?? d?.profile?.commission_pct ?? 0}%</p>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="bg-[#f7faf8] text-left text-slate-500">
              <th className="px-3 py-2 font-semibold">Student</th><th className="px-3 py-2 font-semibold">Country</th>
              <th className="px-3 py-2 font-semibold">Applications</th><th className="px-3 py-2 font-semibold">Latest status</th><th className="px-3 py-2 font-semibold">Commission</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {(refs?.students || []).map((s) => (
                <tr key={s.student_id} data-testid={`referral-${s.student_id}`}>
                  <td className="px-3 py-2"><p className="font-medium text-[#012a1e]">{s.name}</p><p className="text-xs text-slate-400">{s.email}</p></td>
                  <td className="px-3 py-2 text-slate-600">{s.country || "—"}</td>
                  <td className="px-3 py-2 text-slate-600">{s.application_count}</td>
                  <td className="px-3 py-2"><span className="text-xs bg-[#e6f0ec] text-[#004030] px-2 py-0.5 rounded-full">{s.latest_status}</span></td>
                  <td className="px-3 py-2 font-semibold text-[#012a1e]">{money(s.commission, cur)}</td>
                </tr>
              ))}
              {refs && !(refs.students || []).length && <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400">No referred students yet. Share your referral link to start.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "offers" && (
        <div data-testid="agent-offers">
          <div className="flex items-center gap-2 mb-4">
            <Star size={18} className="text-[#c9a227]" />
            <h3 className="font-display text-lg font-bold text-[#012a1e]">Exclusive Partner Universities</h3>
          </div>
          {partners === null && <p className="text-sm text-slate-400 py-6">Loading…</p>}
          {partners && partners.length === 0 && (
            <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-10 text-center">
              <p className="text-slate-400">No exclusive partners yet. Check back soon for special referral offers.</p>
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-4">
            {(partners || []).map((u) => (
              <div key={u.id} data-testid={`partner-card-${u.id}`} className="bg-white rounded-2xl border border-[#e0c05a]/50 p-5 shadow-sm">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-xl bg-[#fffdf5] grid place-items-center text-[#c9a227]"><Award size={20} /></div>
                    <div>
                      <p className="font-display font-bold text-[#012a1e]">{u.name}</p>
                      <p className="text-xs text-slate-500">{[u.city, u.country].filter(Boolean).join(", ")}</p>
                    </div>
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wide bg-[#c9a227] text-[#013d2b] px-2 py-0.5 rounded-full">Exclusive</span>
                </div>
                {u.referral_offer && (
                  <div className="mt-3 flex items-start gap-2 text-sm bg-[#f0f6f3] rounded-xl p-3" data-testid={`partner-offer-${u.id}`}>
                    <Gift size={16} className="text-[#004030] shrink-0 mt-0.5" />
                    <span className="text-slate-700">{u.referral_offer}</span>
                  </div>
                )}
                <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
                  <span className="text-slate-600">Referral code: <b className="text-[#004030]">{u.referral_code || "—"}</b></span>
                  {(u.referral_fee || u.referral_fee_pct) ? (
                    <span className="text-slate-600">Commission: <b className="text-[#004030]">{u.referral_currency || "USD"} {u.referral_fee || 0}{u.referral_fee_pct ? ` (${u.referral_fee_pct}%)` : ""}</b></span>
                  ) : null}
                  {u.referral_code && (
                    <button data-testid={`copy-code-${u.id}`} onClick={() => { navigator.clipboard?.writeText(u.referral_code); toast({ title: "Referral code copied" }); }} className="inline-flex items-center gap-1 text-xs font-semibold text-[#004030] hover:underline"><Copy size={13} /> Copy</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "report" && (
        <div className="space-y-6" data-testid="agent-commission-report">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat icon={TrendingUp} label="Your commission rate" value={`${agentPct}%`} />
            <Stat icon={Clock} label="Pending" value={money(d?.wallet?.pending, cur)} tint="#c9a227" />
            <Stat icon={CheckCircle2} label="Approved" value={money(d?.wallet?.approved_total, cur)} tint="#0a5540" />
            <Stat icon={Wallet} label="Available" value={money(d?.wallet?.available, cur)} />
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 p-5">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
              <div>
                <h2 className="font-display text-lg font-bold text-[#012a1e]">Commission Report</h2>
                <p className="text-sm text-slate-500">Each commission = <span className="font-semibold text-[#004030]">your {agentPct}%</span> × the university's referral fee.</p>
              </div>
              <Button variant="outline" onClick={printReport} data-testid="print-commission-report" className="rounded-full border-[#004030] text-[#004030]">
                <FileText size={15} className="mr-1" /> Print / Download
              </Button>
            </div>
            <div className="overflow-x-auto mt-3">
              <table className="w-full text-sm min-w-[640px]">
                <thead><tr className="bg-[#f7faf8] text-left text-slate-500">
                  <th className="px-3 py-2 font-semibold">Reference</th>
                  <th className="px-3 py-2 font-semibold">Date</th>
                  <th className="px-3 py-2 font-semibold text-right">Referral fee</th>
                  <th className="px-3 py-2 font-semibold text-center">Your %</th>
                  <th className="px-3 py-2 font-semibold text-right">Commission</th>
                  <th className="px-3 py-2 font-semibold">Status</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {(d?.commissions || []).map((c) => {
                    const pct = c.pct ?? agentPct;
                    const hasFee = c.referral_fee !== undefined && c.referral_fee !== null && c.referral_fee !== "";
                    return (
                      <tr key={c.id} data-testid={`report-row-${c.id}`}>
                        <td className="px-3 py-2"><p className="font-medium text-[#012a1e]">{c.reference || "Commission"}</p>{c.referral_fee_pct ? <p className="text-xs text-slate-400">Univ. referral: {c.referral_fee_pct}%</p> : null}</td>
                        <td className="px-3 py-2 text-slate-500">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</td>
                        <td className="px-3 py-2 text-right text-slate-600">{hasFee ? money(c.referral_fee, c.currency || cur) : "—"}</td>
                        <td className="px-3 py-2 text-center text-slate-600">{pct}%</td>
                        <td className="px-3 py-2 text-right font-semibold text-[#012a1e]">{money(c.amount, c.currency || cur)}</td>
                        <td className="px-3 py-2"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${COMM_COLORS[c.status] || "bg-slate-100"}`}>{c.status}</span></td>
                      </tr>
                    );
                  })}
                  {!(d?.commissions || []).length && <tr><td colSpan={6} className="px-3 py-10 text-center text-slate-400">No commissions yet. They accrue automatically when your referred leads convert to applications.</td></tr>}
                </tbody>
                {(d?.commissions || []).length > 0 && (
                  <tfoot><tr className="border-t-2 border-slate-100 font-semibold text-[#012a1e]">
                    <td className="px-3 py-2" colSpan={4}>Approved total</td>
                    <td className="px-3 py-2 text-right">{money(d?.wallet?.approved_total, cur)}</td>
                    <td className="px-3 py-2"></td>
                  </tr></tfoot>
                )}
              </table>
            </div>
          </div>
        </div>
      )}

      {tab === "documents" && (
        <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="agent-documents">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <div>
              <h2 className="font-display text-lg font-bold text-[#012a1e]">My Documents</h2>
              <p className="text-sm text-slate-500">Upload certificates & past-performance proof (PDF, image or Word).</p>
            </div>
            <label className="inline-flex items-center gap-1.5 bg-[#004030] hover:bg-[#013d2b] text-white rounded-full px-5 h-11 cursor-pointer text-sm font-semibold" data-testid="agent-doc-upload-btn">
              {uploading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />} Upload
              <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx" hidden onChange={uploadDoc} />
            </label>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {docs.map((dc) => (
              <div key={dc.id} className="border border-slate-200 rounded-xl p-4 flex items-start gap-3" data-testid={`agent-doc-${dc.id}`}>
                <FileText size={28} className="text-[#004030] shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-[#012a1e] truncate" title={dc.title}>{dc.title}</p>
                  <p className="text-xs text-slate-400">{Math.round((dc.size || 0) / 1024)} KB</p>
                  <div className="flex gap-3 mt-2">
                    <a href={docViewUrl(dc.id)} target="_blank" rel="noreferrer" className="text-[#004030] hover:underline text-xs flex items-center gap-1" data-testid={`agent-doc-view-${dc.id}`}><Eye size={13} /> View</a>
                    <button onClick={() => deleteDoc(dc.id)} className="text-red-500 hover:underline text-xs flex items-center gap-1" data-testid={`agent-doc-delete-${dc.id}`}><Trash2 size={13} /> Delete</button>
                  </div>
                </div>
              </div>
            ))}
            {!docs.length && <p className="text-sm text-slate-400 col-span-full py-6 text-center">No documents uploaded yet.</p>}
          </div>
        </div>
      )}

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">Request a payout</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-500">Available balance: <span className="font-semibold text-[#012a1e]">{money(d?.wallet?.available, cur)}</span></p>
            <div className="space-y-2"><Label>Amount ({cur})</Label>
              <Input data-testid="payout-amount" type="number" min="0" step="0.01" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} className="h-11" placeholder="0.00" /></div>
            <div className="space-y-2"><Label>Payout method</Label>
              <Select value={pay.method} onValueChange={(v) => setPay({ ...pay, method: v })}>
                <SelectTrigger className="h-11" data-testid="payout-method"><SelectValue /></SelectTrigger>
                <SelectContent>{(d?.payout_methods || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Notes (optional)</Label>
              <Input value={pay.notes} onChange={(e) => setPay({ ...pay, notes: e.target.value })} className="h-11" placeholder="Bank / reference details" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Cancel</Button>
            <Button data-testid="payout-submit" onClick={requestPayout} disabled={busy} className="bg-[#004030] hover:bg-[#013d2b] text-white">Request payout</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PortalShell>
  );
}
